import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Trash2, AlertTriangle, MoreVertical, BookOpen, Users } from 'lucide-react';

function BookCard({ book, onClick, onDelete }) {
  const { t } = useTranslation();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  if (!book || !book.book_id) {
    return (
      <div className="story-surface-subtle p-4 flex items-center gap-3 text-[var(--story-muted)]">
        <AlertTriangle className="w-4 h-4 shrink-0 text-[#C4A882] dark:text-[#7A6850]" />
        <span className="text-sm">This book couldn't be loaded.</span>
      </div>
    );
  }

  const toCount = (value) => {
    const count = Number(value);
    return Number.isFinite(count) && count > 0 ? count : 0;
  };

  const chapterCount = toCount(
    book.chapter_count ?? book.chapters_count ?? book.chapter_summaries?.length
  );
  const wordCount = toCount(book.word_count ?? book.total_word_count);
  const bookTypeLabel = book.is_interview_mode ? 'Interview book' : null;
  
  const getGenreColor = (genre) => {
    const colors = {
      memoir: 'bg-[#F6EFE8] dark:bg-[#2A241F] text-[#7A6250] dark:text-[#CDBBA9] border-[#E6D9C9] dark:border-[#40362E]',
      fiction: 'bg-[#F2EDF6] dark:bg-[#2B2430] text-[#665174] dark:text-[#CDBCE0] border-[#E1D5E9] dark:border-[#463A50]',
      business: 'bg-[#EEF2F4] dark:bg-[#222A2E] text-[#59676C] dark:text-[#B9C7CC] border-[#D8E1E5] dark:border-[#39454B]',
      childrens: 'bg-[#F7EFE5] dark:bg-[#2A241F] text-[#755F4A] dark:text-[#D3BDA4] border-[#E7D8C6] dark:border-[#40362E]',
      freestyle: 'bg-[#EEF4EA] dark:bg-[#222A20] text-[#5B6C52] dark:text-[#B8C8AD] border-[#D7E3D0] dark:border-[#384433]'
    };
    return colors[genre] || colors.freestyle;
  };

  const getGenreLabel = (genre) => {
    const normalizedGenre = genre || 'freestyle';
    const genreKey = normalizedGenre === 'childrens' ? 'childrens' : normalizedGenre;
    return t(`genres.${genreKey}`, normalizedGenre.charAt(0).toUpperCase() + normalizedGenre.slice(1));
  };

  const handleMenuClick = (e) => { e.stopPropagation(); setShowMenu(!showMenu); };
  const handleDeleteClick = (e) => { e.stopPropagation(); setShowMenu(false); setShowDeleteConfirm(true); };
  const handleConfirmDelete = (e) => { e.stopPropagation(); onDelete(book.book_id); setShowDeleteConfirm(false); };
  const handleCancelDelete = (e) => { e.stopPropagation(); setShowDeleteConfirm(false); };

  return (
    <>
      <div
        onClick={onClick}
        data-testid={`book-card-${book.book_id}`}
        className="story-surface p-3.5 sm:p-4 hover:border-[var(--story-line-strong)] transition-all duration-200 cursor-pointer group active:scale-[0.99] touch-target"
      >
        {/* Title row */}
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <div className="min-w-0 flex-1">
            <h3 className={`font-serif text-base font-semibold leading-snug line-clamp-1 ${book.title === 'Untitled Book' ? 'text-[#8C6E48] dark:text-[#E8C98F]' : 'text-[#2A2420] dark:text-[#FFF8EF]'}`}>{book.title}</h3>
          </div>
          <div className="relative flex-shrink-0 -mt-0.5 -mr-1">
            <button
              onClick={handleMenuClick}
              className="opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity text-[#A09890] dark:text-[#CFC3B7] hover:text-[#4F415D] hover:bg-[#F0EBE4]/80 dark:hover:!bg-[#2B2621] min-h-[36px] min-w-[36px] rounded-lg flex items-center justify-center"
              aria-label="Book actions"
            >
              <MoreVertical className="w-3.5 h-3.5" />
            </button>
            {showMenu && (
              <div className="absolute right-0 top-10 w-36 bg-[#FFFCF7] dark:!bg-[#211E1A] border border-[#E0D8CC] dark:!border-[#3A332C] rounded-xl shadow-lg z-50 overflow-hidden">
                <button
                  onClick={(e) => { e.stopPropagation(); setShowMenu(false); onClick?.(); }}
                  className="w-full px-3.5 py-2.5 text-left text-sm text-[#2A2420] dark:text-[#F0EBE4] hover:bg-[#F0EBE4]/60 dark:hover:!bg-[#2B2621] flex items-center gap-2"
                >
                  <BookOpen className="w-3.5 h-3.5 text-[#5C4D6B]" />
                  Open
                </button>
                {onDelete && (
                  <button
                    onClick={handleDeleteClick}
                    data-testid={`delete-book-${book.book_id}`}
                    className="w-full px-3.5 py-2.5 text-left text-sm text-red-500 dark:text-red-300 hover:bg-red-50 dark:hover:!bg-red-900/20 flex items-center gap-2 border-t border-[#E0D8CC] dark:!border-[#3A332C]"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Delete
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Genre + stats row */}
        <div className="flex items-center gap-2 flex-wrap mb-1.5">
          <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium border ${getGenreColor(book.genre)}`}>
            {getGenreLabel(book.genre)}
          </span>
          {bookTypeLabel && (
            <span className="text-[11px] font-medium text-[#6B5A7B] dark:text-[#D9C9E8]">{bookTypeLabel}</span>
          )}
          <span className="text-[11px] text-[#8C8176] dark:text-[#B8AEA4]">
            {chapterCount.toLocaleString()} {chapterCount === 1 ? 'chapter' : 'chapters'} · {wordCount.toLocaleString()} {wordCount === 1 ? 'word' : 'words'}
          </span>
        </div>

        {/* Description */}
        {book.description && (
          <p className="text-xs text-[#7C766E] dark:text-[#A89E94] line-clamp-1 leading-relaxed mb-1">{book.description}</p>
        )}

        {/* Footer: created date + shared by */}
        <div className="flex items-center justify-between gap-2 mt-1.5">
          <span className="text-[10px] text-[#A09890] dark:text-[#706860]">{t('book.created')} {new Date(book.created_at).toLocaleDateString()}</span>
          {book.is_shared && book.owner_name && (
            <span className="flex items-center gap-1 text-[10px] text-[#8C847A] dark:text-[#B8AEA4]">
              <Users className="w-2.5 h-2.5" />
              <span className="truncate max-w-[100px]">Shared by {book.owner_name}</span>
            </span>
          )}
        </div>
      </div>

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={handleCancelDelete}>
          <div className="bg-white dark:bg-[#22201C] rounded-2xl p-6 max-w-sm w-full shadow-[0_20px_60px_-15px_rgba(0,0,0,0.15)]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 dark:bg-red-900/20 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-500" />
              </div>
              <h3 className="font-serif text-lg font-semibold text-[#2A2420] dark:text-[#F0EBE4]">{t('modal.deleteBook')}</h3>
            </div>
            <p className="text-sm text-[#6B6560] dark:text-[#9C9590] mb-6 leading-relaxed">
              {t('modal.deleteConfirm')} "<span className="font-medium text-[#2A2420] dark:text-[#F0EBE4]">{book.title}</span>"? {t('modal.cannotUndo')}
            </p>
            <div className="flex gap-3">
              <Button onClick={handleCancelDelete} variant="outline" className="flex-1 rounded-full" data-testid="cancel-delete-btn">{t('modal.cancel')}</Button>
              <Button onClick={handleConfirmDelete} className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-full" data-testid="confirm-delete-btn">{t('buttons.delete')}</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default BookCard;
